/**
 * Account + Team tab of the Admin User Editor (AR-327) — company and plan,
 * the agency roster around this member, their Stripe subscription, and the
 * account-level document set.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, Plus } from 'lucide-react';
import {
  DeferredAction,
  FieldGrid,
  PendingIntegration,
  ReadOnlyField,
  RecordCard,
  SelectField,
  StatePill,
  TextField,
} from './recordPrimitives';
import { MemberAvatar } from '@/components/MemberAvatar';
import { roleLabel } from './ProfileTab';
import type { AdminUserRecord } from '@/lib/adminUserRecordApi';
import type { RecordDraft } from './useRecordDraft';

const TEAM_ROLES = ['ADVISOR', 'AGENCY_MANAGER', 'AGENCY_OWNER'];

type ActionKey =
  | 'create-company'
  | 'add-member'
  | 'save-member'
  | 'cancel-subscription'
  | 'delete-hubspot'
  | null;

interface MemberEdit {
  id: number;
  name: string;
  role: string;
}

function TeamMemberRow({
  name,
  email,
  photoUrl,
  role,
  editing,
  draftName,
  draftRole,
  onDraftName,
  onDraftRole,
  onEdit,
  onSave,
  onCancel,
}: {
  name: string;
  email: string;
  photoUrl: string | null;
  role: string | null;
  editing: boolean;
  draftName: string;
  draftRole: string;
  onDraftName: (value: string) => void;
  onDraftRole: (value: string) => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  if (editing) {
    return (
      <li className="rounded-lg border border-slate-200 p-3 dark:border-gray-700">
        <div className="flex flex-col gap-2">
          <Input
            aria-label={`Name for ${name}`}
            value={draftName}
            onChange={(event) => onDraftName(event.target.value)}
            className="h-9 text-sm"
          />
          <Select value={draftRole} onValueChange={onDraftRole}>
            <SelectTrigger className="h-9 text-sm" aria-label={`Role for ${name}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEAM_ROLES.map((slug) => (
                <SelectItem key={slug} value={slug}>
                  {roleLabel(slug)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button size="sm" onClick={onSave}>
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-gray-700">
      {/* AR-409: the shared member avatar. A teammate's own theme is not known
          here, so the circle takes the default colour. */}
      <MemberAvatar
        photoUrl={photoUrl}
        displayName={name}
        email={email}
        colorTheme={null}
        className="h-9 w-9 text-xs"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{name}</p>
        <p className="truncate text-xs text-slate-500 dark:text-slate-400">{email}</p>
      </div>
      <StatePill tone={role === 'AGENCY_OWNER' ? 'dark' : role === 'AGENCY_MANAGER' ? 'accent' : 'neutral'}>
        {roleLabel(role)}
      </StatePill>
      <Button size="sm" variant="outline" onClick={onEdit}>
        Edit
      </Button>
    </li>
  );
}

export function AccountTeamTab({
  record,
  draft,
}: {
  record: AdminUserRecord;
  draft: RecordDraft;
}) {
  const [action, setAction] = useState<ActionKey>(null);
  const [editing, setEditing] = useState<MemberEdit | null>(null);

  const isOwner =
    !record.user.parentUserId &&
    (record.team?.agencyOwnerUserId ?? record.user.id) === record.user.id;
  const planNames = record.availablePlans.map((plan) => plan.name);

  return (
    <div className="flex flex-col gap-5">
      <RecordCard
        title="Company + Membership"
        action={
          <Button variant="outline" size="sm" onClick={() => setAction('create-company')}>
            <Building2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Create Company
          </Button>
        }
      >
        <FieldGrid>
          <SelectField
            label="Membership Plan"
            value={draft.text('account.membershipPlan')}
            readOnly={!draft.editable('account.membershipPlan')}
            options={planNames.length > 0 ? planNames : [draft.text('account.membershipPlan')].filter(Boolean)}
            onChange={draft.setText('account.membershipPlan')}
          />
          <TextField
            label="Active GEM Status"
            value={draft.text('account.gemStatus')}
            readOnly={!draft.editable('account.gemStatus')}
            onChange={draft.setText('account.gemStatus')}
          />
          <TextField
            label="Company Legal Entity Name"
            value={draft.text('account.legalEntityName')}
            readOnly={!draft.editable('account.legalEntityName')}
            onChange={draft.setText('account.legalEntityName')}
          />
          <TextField
            label="Travel Agency Brand Name"
            value={draft.text('account.brandName')}
            readOnly={!draft.editable('account.brandName')}
            onChange={draft.setText('account.brandName')}
          />
          <TextField
            label="EIN"
            value={draft.text('account.ein')}
            readOnly={!draft.editable('account.ein')}
            onChange={draft.setText('account.ein')}
          />
          <TextField
            label="Referred By"
            value={draft.text('account.referredBy')}
            readOnly={!draft.editable('account.referredBy')}
            onChange={draft.setText('account.referredBy')}
          />
        </FieldGrid>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Creating a company only makes the record — linking it to this member happens through the
          Company dropdown on save.
        </p>
      </RecordCard>

      <RecordCard
        title="Team Structure"
        description="Who this member reports to, and who reports to them."
        action={
          <Button variant="outline" size="sm" onClick={() => setAction('add-member')}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Add Team Member
          </Button>
        }
      >
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 px-4 py-3 dark:bg-gray-900">
          <StatePill tone="dark">{roleLabel(record.user.roleSlug ?? record.user.role)}</StatePill>
          {isOwner ? (
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Owns {record.team?.name ?? 'this agency'} — {record.members.length} team member
              {record.members.length === 1 ? '' : 's'}
            </span>
          ) : (
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Reports to{' '}
              <strong className="font-semibold">{record.owner?.displayName ?? 'an agency owner'}</strong>
              {record.owner?.email ? ` (${record.owner.email})` : ''}
            </span>
          )}
        </div>

        {record.members.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No other members on this agency.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-2 min-[1800px]:grid-cols-2">
            {record.members.map((member) => (
              <TeamMemberRow
                key={member.id}
                name={member.displayName}
                email={member.email}
                photoUrl={member.avatarUrl}
                role={member.roleSlug}
                editing={editing?.id === member.id}
                draftName={editing?.id === member.id ? editing.name : member.displayName}
                draftRole={editing?.id === member.id ? editing.role : member.roleSlug ?? 'ADVISOR'}
                onDraftName={(value) =>
                  setEditing((prev) => (prev ? { ...prev, name: value } : prev))
                }
                onDraftRole={(value) =>
                  setEditing((prev) => (prev ? { ...prev, role: value } : prev))
                }
                onEdit={() =>
                  setEditing({
                    id: member.id,
                    name: member.displayName,
                    role: member.roleSlug ?? 'ADVISOR',
                  })
                }
                onSave={() => setAction('save-member')}
                onCancel={() => setEditing(null)}
              />
            ))}
          </ul>
        )}
      </RecordCard>

      <RecordCard title="Subscription + Payment" description="Read-only — sourced from Stripe.">
        <FieldGrid>
          <ReadOnlyField label="Subscription Status" value={record.subscription?.status ?? null} />
          <ReadOnlyField
            label="Stripe Customer"
            value={record.subscription?.stripeCustomerId ?? record.user.stripeCustomerId}
          />
          <ReadOnlyField
            label="Stripe Subscription"
            value={record.subscription?.stripeSubscriptionId ?? record.user.stripeSubscriptionId}
          />
        </FieldGrid>
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Period Start · Period End · Cancel at Period End
          </p>
          <PendingIntegration source="the Stripe subscription-period feed" />
        </div>
        <div className="mt-4 flex flex-wrap gap-4">
          <Button
            variant="link"
            className="h-auto p-0 text-sm font-semibold text-orange-700 dark:text-orange-400"
            onClick={() => setAction('cancel-subscription')}
          >
            Cancel Subscription at Period End
          </Button>
          <Button
            variant="link"
            className="h-auto p-0 text-sm font-semibold text-rose-700 dark:text-rose-400"
            onClick={() => setAction('delete-hubspot')}
          >
            Delete HubSpot Account
          </Button>
        </div>
      </RecordCard>

      <RecordCard
        title="Documents + Storage"
        description="Account-level records — also referenced during member activation."
      >
        <PendingIntegration source="the onboarding document store" />
      </RecordCard>

      <RecordCard
        title="Plan History"
        description="Plan changes and subscription errors, newest first."
      >
        <PendingIntegration source="the Subscription Log feed" />
      </RecordCard>

      <DeferredAction
        open={action === 'create-company'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Create Company"
        description="Creates the company record only. Link it to this member with the Company dropdown, then save."
        confirmLabel="Create company"
        fields={[
          { name: 'type', label: 'Company Type', placeholder: 'e.g. Independent Agency' },
          { name: 'legal', label: 'Legal Entity Name' },
          { name: 'trade', label: 'Trade Name' },
        ]}
        ticketNote="Company creation is a companion backend ticket."
      />
      <DeferredAction
        open={action === 'save-member'}
        onOpenChange={(open) => {
          if (!open) {
            setAction(null);
            setEditing(null);
          }
        }}
        title="Update team member"
        description={
          editing
            ? `Sets ${editing.name} to ${roleLabel(editing.role)} on this agency.`
            : 'Updates this team member.'
        }
        confirmLabel="Save member"
        ticketNote="Team member updates are a companion backend ticket."
      />
      <DeferredAction
        open={action === 'add-member'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Add Team Member"
        description="Invites an associate advisor onto this agency."
        confirmLabel="Add member"
        fields={[
          { name: 'name', label: 'Full Name' },
          { name: 'email', label: 'Email', type: 'email' },
        ]}
        ticketNote="Team member provisioning is a companion backend ticket."
      />
      <DeferredAction
        open={action === 'cancel-subscription'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Cancel membership at period end"
        description="Calls Stripe to cancel at period end and writes a Subscription Log entry. The member keeps access until the current period closes."
        confirmLabel="Cancel membership"
        destructive
        confirmPhrase="CANCEL"
        ticketNote="The Stripe cancel call and Subscription Log entry are a companion backend ticket."
      />
      <DeferredAction
        open={action === 'delete-hubspot'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Delete HubSpot account"
        description="Calls delete_account, clears the HubSpot ID, and marks this user as a test account. Only members need HubSpot accounts — team members and test accounts do not."
        confirmLabel="Delete HubSpot account"
        destructive
        confirmPhrase="DELETE"
        ticketNote="The HubSpot delete_account call is a companion backend ticket."
      />
    </div>
  );
}
